import React, { useState, useEffect } from "react";
import { connect } from "react-redux";

import Modal from "../common_modal/Modal";
import { set_value_properties } from "../../App/actions/propertiesActions";
import { post_activities } from "../../App/actions/activities";

import useLanguage from "../common_hooks/useLanguage";

const GUIDE_STEPS = [
  {
    id: "home_ui",
    title: "Home",
    description: "Return to the main dashboard.",
  },
  {
    id: "search_ui",
    title: "Search",
    description: "Find locations or data layers seamlessly.",
  },
  {
    id: "draw_ui",
    title: "Draw Tools",
    description:
      "Create and edit points, lines, or polygons directly on the map.",
  },
  {
    id: "share_ui",
    title: "Share",
    description: "Generate a link to share your current project view.",
  },
  {
    id: "sidebar_right_ui",
    title: "Sidebar",
    description: "Toggle the right sidebar for detailed data panels.",
  },
  {
    id: "mapid_apps_ui",
    title: "MAPID APPS",
    description:
      "Access integrated applications, activities, and photo heatmaps.",
  },
  {
    id: "gps_ui",
    title: "GPS Location",
    description: "Center the map exactly on your current physical location.",
  },
  {
    id: "compass_ui",
    title: "Compass",
    description: "Reset map bearing and pitch to face true North.",
  },
  {
    id: "zoom_ui",
    title: "Zoom Controls",
    description: "Adjust the map scale precisely.",
  },
  {
    id: "setting_ui",
    title: "Settings",
    description: "Change basemaps, language, map projection, and more.",
  },
  {
    id: "feedback_ui",
    title: "Feedback",
    description: "Send us your thoughts, bug reports, or feature requests.",
  },
  {
    id: "scale_bar_ui",
    title: "Scale Bar",
    description: "View map distances and copy your current coordinates.",
  },
];

const MAP_GUIDE = ({ properties, set_value_properties, post_activities }) => {
  // We grab these from Redux so ANY separated component can trigger the guide
  const guideActive = properties.guideActive || false;
  const showGuideModal = properties.showGuideModal || false;

  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  const { dict, language } = useLanguage();

  // --- GUIDE INIT EFFECT ---
  useEffect(() => {
    const isCompleted = localStorage.getItem("userGuideCompleted");
    // Only show if not completed AND we haven't already set it in Redux this session
    if (!isCompleted && properties.showGuideModal === undefined) {
      set_value_properties({ key: "showGuideModal", value: true });
    }
  }, [properties.showGuideModal, set_value_properties]);

  const startGuide = () => {
    set_value_properties({ key: "showGuideModal", value: false });
    set_value_properties({ key: "guideActive", value: true });
    setCurrentStep(0);
    post_activities({
      action: "start_map_guide",
    });
  };

  const skipGuide = () => {
    set_value_properties({ key: "showGuideModal", value: false });
    set_value_properties({ key: "guideActive", value: false });
    localStorage.setItem("userGuideCompleted", "true");
    setCurrentStep(0);
    post_activities({
      action: "skip_map_guide",
    });
  };

  const nextStep = () => {
    if (currentStep < GUIDE_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      set_value_properties({ key: "guideActive", value: false });
      localStorage.setItem("userGuideCompleted", "true");
      setCurrentStep(0);
      post_activities({
        action: "finish_map_guide",
      });
    }
  };

  const backStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // --- GUIDE TARGET TRACKING ---
  useEffect(() => {
    if (guideActive) {
      const updatePosition = () => {
        let stepIndex = currentStep;
        let el = document.getElementById(GUIDE_STEPS[stepIndex].id);

        // Skip items that might not render conditionally (like sidebar on mobile)
        while (!el && stepIndex < GUIDE_STEPS.length - 1) {
          stepIndex++;
          el = document.getElementById(GUIDE_STEPS[stepIndex].id);
        }

        if (el) {
          if (stepIndex !== currentStep) setCurrentStep(stepIndex);
          setTargetRect(el.getBoundingClientRect());
        } else {
          // If we run out of steps, end the guide
          set_value_properties({ key: "guideActive", value: false });
          localStorage.setItem("userGuideCompleted", "true");
        }
      };

      updatePosition();
      window.addEventListener("resize", updatePosition);
      // Added scroll listener just in case layout shifts dynamically
      window.addEventListener("scroll", updatePosition, true);

      return () => {
        window.removeEventListener("resize", updatePosition);
        window.removeEventListener("scroll", updatePosition, true);
      };
    }
  }, [guideActive, currentStep, set_value_properties]);

  return (
    <>
      {showGuideModal && (
        <Modal modalSize="small" isOpen={showGuideModal} onClose={skipGuide}>
          <div
            className="box-body"
            style={{ textAlign: "center", padding: "20px" }}
          >
            <h3 className="text_bold margin_bottom">
              {dict["Discover New GEO MAPID"][language]}
            </h3>
            <p className="margin_bottom">
              {dict["Would you like a quick tour of the features?"][language]}
            </p>
            <div
              style={{ display: "flex", justifyContent: "center", gap: "10px" }}
            >
              <button className="button background_black" onClick={startGuide}>
                {dict["Start Tour"][language]}
              </button>
              <button
                className="button button_white outline_grey"
                onClick={skipGuide}
              >
                {dict["Skip"][language]}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {guideActive && targetRect && (
        <div className="guide_overlay_wrapper">
          {/* Box Shadow Highlight Cutout */}
          <div
            className="guide_highlight_cutout"
            style={{
              left: targetRect.left - 5,
              top: targetRect.top - 5,
              width: targetRect.width + 10,
              height: targetRect.height + 10,
            }}
          />

          {/* Tooltip Card */}
          <div
            className="guide_tooltip_card"
            style={{
              // Prevent tooltip from going off screen horizontally
              left: Math.min(
                Math.max(10, targetRect.left + targetRect.width / 2 - 146),
                window.innerWidth - 310,
              ),
              // Place below the target, unless it hits bottom of screen
              top:
                targetRect.bottom + 180 > window.innerHeight
                  ? Math.max(10, targetRect.top - 180)
                  : targetRect.bottom + 15,
            }}
          >
            <div className="guide_tooltip_content">
              <div className="flex justify_between">
                <h4>{dict[GUIDE_STEPS[currentStep].title][language]}</h4>
                <button
                  className="button_circle background_black"
                  onClick={skipGuide}
                >
                  ✖
                </button>
              </div>
              <p>{dict[GUIDE_STEPS[currentStep].description][language]}</p>
            </div>
            <div className="guide_tooltip_actions">
              <span className="guide_tooltip_progress">
                {currentStep + 1} / {GUIDE_STEPS.length}
              </span>
              <div className="flex gap_10">
                {currentStep > 0 && (
                  <button
                    className="button background_black"
                    onClick={backStep}
                  >
                    {dict["Back"][language]}
                  </button>
                )}
                <button className="button background_black" onClick={nextStep}>
                  {currentStep === GUIDE_STEPS.length - 1
                    ? dict["Finish"][language]
                    : dict["Next"][language]}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  properties: state.properties,
});

export default connect(mapStateToProps, {
  set_value_properties,
  post_activities,
})(MAP_GUIDE);
